import React, { FC, useEffect } from 'react';
import {
    ControlledSelect,
    ISupportTypeFrequenciesForm,
    Page,
    PermissionKeys,
    useHasPermission,
    useSelectedOrganisation,
    Widget,
} from '../../../shared';
import { Button, MenuItem, Stack, TextField } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Frequency } from '../../../support/enums';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useSupportTypeFrequencySchema } from '../../validators';
import { supportTypeFrequencyFromFormMapper, supportTypeFrequencyToFormMapper } from '../../mappers';
import { useSaveSupportTypesFrequency, useSupportTypesFrequencyList } from '../../hooks/support-type-frequency.hook';
import { useOrganisation } from '../../hooks/organisation.hook';

export const SupportTypesFrequenciesEditPage: FC = () => {
    const { t } = useTranslation();
    const { organisation: currentOrganisation } = useSelectedOrganisation();

    const { hasPermission } = useHasPermission();
    const hasWritePermission = hasPermission(PermissionKeys.SUPPORT_TYPE_FREQUENCIES_WRITE);

    const { data: organisation } = useOrganisation(currentOrganisation?.id);
    const { mutateAsync: saveSupportTypesFrequencies } = useSaveSupportTypesFrequency();

    const { data: supportTypesFrequencies } = useSupportTypesFrequencyList(
        { organisationId: organisation?.id },
        { messages: { onGetErrorMessage: t('getSupportTypesFrequenciesError') } },
    );

    const form = useForm<ISupportTypeFrequenciesForm>({
        resolver: yupResolver(useSupportTypeFrequencySchema()),
        mode: 'all',
    });

    useEffect(() => {
        if (organisation) {
            form.reset(supportTypeFrequencyToFormMapper(organisation, supportTypesFrequencies?.data));
        }
    }, [organisation, form, supportTypesFrequencies]);

    const onSubmit = async (data: ISupportTypeFrequenciesForm) => {
        if (organisation) {
            await saveSupportTypesFrequencies({
                data: supportTypeFrequencyFromFormMapper(organisation, data),
                messages: {
                    onCreateSuccessMessage: t('updateSupportTypesFrequenciesSuccess'),
                    onCreateErrorMessage: t('updateSupportTypesFrequenciesError'),
                },
            });
        }
    };

    return (
        <FormProvider {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} noValidate>
                <Page
                    title={t('supportTypesFrequencies')}
                    actions={[
                        hasWritePermission && (
                            <Button color="primary" variant="contained" onClick={form.handleSubmit(onSubmit)}>
                                {t('save')}
                            </Button>
                        ),
                    ]}
                >
                    {organisation && (
                        <Widget>
                            <Stack spacing={2}>
                                {form.getValues('frequencies')?.map((_, i) => (
                                    <Stack direction="row" spacing={2} key={i}>
                                        <Controller
                                            name={`frequencies.${i}.supportType.name`}
                                            control={form.control}
                                            defaultValue=""
                                            render={({ field: { onChange, value } }) => (
                                                <TextField
                                                    value={value}
                                                    onChange={onChange}
                                                    label={t('supportType')}
                                                    slotProps={{ input: { readOnly: true } }}
                                                />
                                            )}
                                        />

                                        <ControlledSelect
                                            name={`frequencies.${i}.frequency`}
                                            label={t('frequency')}
                                            disabled={!hasWritePermission}
                                        >
                                            <MenuItem key={''} value={''} sx={{ height: '35px' }} />
                                            {Object.values(Frequency).map((frequency) => (
                                                <MenuItem key={frequency} value={frequency}>
                                                    {t(`${frequency}`)}
                                                </MenuItem>
                                            ))}
                                        </ControlledSelect>
                                    </Stack>
                                ))}
                            </Stack>
                        </Widget>
                    )}
                </Page>
            </form>
        </FormProvider>
    );
};
